import type { TimezoneInfo } from "@src/interfaces/timezone.interface";
import { callApi } from "@src/utils/api-client";
import { getFailureTypeForHttpStatus } from "@src/utils/failure-taxonomy";
import ClientLogsService from "./client-logs.service";
import { buildGetTimezoneConfig } from "./configs/get-timezone.config";

export type TimezoneApiResponse =
  | BackendTimezoneResponse
  | {
      message?: string;
      error?: string;
    };

export type { TimezoneRequest } from "@src/interfaces/timezone.interface";

export interface BackendTimezoneResponse {
  message: {
    timezone_standard_name: string;
    timezone_generic_name: string;
    timezone_abbreviation: string;
    utc_offset: string;
  };
  transactionid: string;
}

export interface TimezoneServiceResponse {
  success: boolean;
  data?: TimezoneInfo;
  error?: string;
}

function isBackendTimezoneResponse(result: TimezoneApiResponse): result is BackendTimezoneResponse {
  return (
    typeof result === "object" &&
    result !== null &&
    "message" in result &&
    typeof result.message === "object" &&
    result.message !== null &&
    "timezone_standard_name" in result.message &&
    "timezone_generic_name" in result.message &&
    "timezone_abbreviation" in result.message &&
    "utc_offset" in result.message
  );
}

function getTimezoneErrorMessage(result: TimezoneApiResponse): string {
  if (typeof result === "object" && result !== null) {
    if ("message" in result && typeof result.message === "string") {
      return result.message;
    }

    if ("error" in result && typeof result.error === "string") {
      return result.error;
    }
  }

  return "Failed to fetch user timezone";
}

function mapBackendTimezoneToTimezoneInfo(response: BackendTimezoneResponse): TimezoneInfo {
  return {
    name: response.message.timezone_abbreviation,
    standardName: response.message.timezone_standard_name,
    genericName: response.message.timezone_generic_name,
    offset: response.message.utc_offset,
  };
}

export async function getTimezoneData(
  userLatitude: number,
  userLongitude: number,
  userUuid: string,
): Promise<TimezoneServiceResponse> {
  try {
    const { data, response } = await callApi(
      buildGetTimezoneConfig(userLatitude, userLongitude, userUuid),
    );

    const result = data as TimezoneApiResponse;

    if (!response.ok) {
      const apiMessage = getTimezoneErrorMessage(result);

      ClientLogsService.logError(
        userUuid,
        "get-timezone.service.getTimezoneData",
        "Error fetching user timezone",
        new Error(apiMessage),
        {
          errorType: getFailureTypeForHttpStatus(response.status),
          dependencyName: "TimezoneAPI",
          functionName: "getTimezoneData",
        },
      );

      return {
        success: false,
        error: apiMessage,
      };
    }

    if (!isBackendTimezoneResponse(result)) {
      const errorMessage = "Invalid timezone response payload";

      ClientLogsService.logError(
        userUuid,
        "get-timezone.service.getTimezoneData",
        "Error fetching user timezone",
        new Error(errorMessage),
        {
          errorType: "TerminalFailure",
          dependencyName: "TimezoneAPI",
          functionName: "getTimezoneData",
        },
      );

      return {
        success: false,
        error: errorMessage,
      };
    }

    const timezoneData = mapBackendTimezoneToTimezoneInfo(result);
    ClientLogsService.logWebAction(
      userUuid,
      "get-timezone.service.getTimezoneData",
      "User timezone fetched successfully",
      undefined,
      { value: result },
    );

    return {
      success: true,
      data: timezoneData,
    };
  } catch (error) {
    ClientLogsService.logError(
      userUuid,
      "get-timezone.service.getTimezoneData",
      "Error fetching user timezone",
      error instanceof Error ? error : new Error(error?.message || "Failed to fetch user timezone"),
      {
        errorType: "DependencyFailure",
        dependencyName: "TimezoneAPI",
        functionName: "getTimezoneData",
      },
    );

    return {
      success: false,
      error:
        error instanceof Error ? error.message : error?.message || "Failed to fetch user timezone",
    };
  }
}
